import React, { ReactElement, useState, useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Form, Button, InputGroup } from "react-bootstrap";
import { Formik } from "formik";
import { AccountType, ActionHandlerFunction } from "@preveil-api";
import { account_types, useGetUsersTypeQuery, PublicRoutes, LoginWebUIActionTypes, FormValidationLimits, AccountErrorMessages } from "src/common";
import { Icon } from "src/components";
import * as yup from "yup";

type AllProps = {
  user_id: string;
  children?: ReactElement;
  handleAction: ActionHandlerFunction;
  is_login?: boolean;
}

function LoginPasswordFormComponent(props: AllProps) {
  const { user_id, children, handleAction, is_login } = props;
  const navigate = useNavigate();
  const [show_password, setShowPassword] = useState<boolean>(false);
  const cancel_button_ref = useRef<HTMLButtonElement>(null);
  const { data } = useGetUsersTypeQuery({ userId: user_id });
  const schema = yup.object().shape({
    password: yup.string()
      .required(AccountErrorMessages.password_required_error)
      .min(
        FormValidationLimits.PASSWORD_MIN_LENGTH,
        AccountErrorMessages.password_helper
      )
  });

  // Description: Handle form submit 
  function handleSubmit(form_values: { password: string; }) {
    handleAction({ actionType: LoginWebUIActionTypes.SubmitPassword, params: form_values.password });
  }

  // Description: Data has been fetched and this handles returned account type
  function AccountTypeHandler(props: { account_type: AccountType }) {
    const _user_id = user_id ? encodeURIComponent(user_id) : "";
    switch (props.account_type) {
      case account_types.full:
        return <Navigate to={`/${PublicRoutes.connect_account_route}?user_id=${_user_id}`} />;
      default:
        return <Navigate to={`/${PublicRoutes.create_account_route}?user_id=${_user_id}`} />;
    }
  }

  return !!data ? data.account_type === account_types.express ?
    <>
      {children}
      <Formik
        validationSchema={schema}
        onSubmit={handleSubmit}
        initialValues={{
          password: "",
          confirmPassword: ""
        }} >
        {({ dirty, values, errors, touched, handleChange, handleSubmit, handleBlur, isValid }) => (
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-4">
              <Form.Label htmlFor="password_tb" className="fs-6">Password</Form.Label>
              <InputGroup className={`password-form-control ${!!errors.password && touched.password ? "invalid" : ""}`} hasValidation={true}>
                <Icon className="ficon-lock" />
                <Form.Control type={!!show_password ? "text" : "password"}
                  id="password_tb"
                  autoFocus={true}
                  placeholder="Enter Password"
                  name="password"
                  value={values.password}
                  onBlur={(e) => {
                    if (cancel_button_ref.current && cancel_button_ref.current.contains(e.relatedTarget as Node)) { return; }
                    handleBlur(e);
                  }}
                  onChange={handleChange}
                  isInvalid={!!errors.password && touched.password} />
                <Button
                  variant="transparent"
                  className="btn-icon"
                  size="sm"
                  onClick={() => setShowPassword(!show_password)}
                  aria-label="show password">
                  <span>
                    <Icon className={!!show_password ? "ficon-eye" : "ficon-eye-off"} />
                  </span>
                </Button>
              </InputGroup>
              <Form.Control.Feedback type="invalid" className={`${!!errors.password && touched.password ? "d-block" : ""}`}>
                * {errors.password}</Form.Control.Feedback>
            </Form.Group>
            <div className="btn-panel">
              <Button type="submit" disabled={!dirty || !isValid} >Continue</Button>
              <Button ref={cancel_button_ref} variant="outline-secondary" type="button"
                onClick={() => handleAction({ actionType: LoginWebUIActionTypes.ResetLogin })}>Cancel</Button>
              {
                is_login &&
                <Button variant="link" type="button" className="d-block px-0 fs-6" onMouseDown={(e) => e.preventDefault()}
                  onClick={() => navigate(`/${PublicRoutes.forgot_password_route}?user_id=${encodeURIComponent(user_id)}`)}>Forgot your password?</Button>
              }
            </div>
          </Form>
        )}
      </Formik>
    </> : <AccountTypeHandler account_type={data.account_type} /> : null;
}
export default React.memo(LoginPasswordFormComponent);
